import React, { JSX } from 'react';
import { colors, fonts} from '../../Styles';
import { progressColor } from '../../Definitions';
import { styled } from 'styletron-react';

interface ProgressCircle_Props {
  total?: number
  progressed?: number
  header?: string
  unit?: JSX.Element
  animation?: boolean
}
/**
 * ProgressCircle
 * @param total The total value representing 100% progress.
 * @param progressed The current progress value towards the total.
 * @param header The optional title displayed above the progress circle.
 * @param unit An optional JSX element displayed below the progress circle.
 * @param animation Enables a smooth animation when the progress value updates.
 * @returns A circular progress circle with an optional header and unit display.
 */
export default function ProgressCircle ({total = 0, progressed = 0, header, animation = false, unit}:ProgressCircle_Props) {
  
  const progressedPercentage = total > 0 ? (progressed / total) * 100 : 0;

  const strokeDasharray = `${progressedPercentage}, 100`;

  const progressAnimation = {
    "0%": { strokeDasharray: "0, 100" },
    "100%": { strokeDasharray: `${progressedPercentage}, 100`}
  };

  // #region | Circle SVG and style
  const BackgroundPath = styled("path", {
    stroke: colors.greyTertiary,
    fill: "none",
    strokeWidth: "3.6px"
  });

  const AnimatedPath = styled("path", {
    stroke: progressColor(progressedPercentage),
    fill: "none",
    strokeWidth: "4px",
    strokeLinecap: "round",
    strokeDasharray: strokeDasharray,
    animationName: animation ? progressAnimation : "unset",
    animationDuration: "2s",
    animationTimingFunction: "ease",
    animationFillMode: "forwards"
  });

  const circle = (
    <svg viewBox="0 0 36 36" style={{
        display: "block",
        maxHeight: "100px"
    }}>
      <BackgroundPath d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/> 
      <AnimatedPath d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
    </svg>
  )
  // #endregion

  return ( 
    <div style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "end", gap: "10px", height: "170px"}}>
      <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary, textAlign: "center"}}>{header}</p>
      <div style={{position: "relative", width: "100px", height: "100px"}}>
        {circle}
        <div style={{ 
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          ...fonts.header,
        }}>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
            <p style={{ ...fonts.fontSize1, color: progressColor(progressedPercentage), margin: "0px" }}>{`${progressed}`}</p>
            <p style={{ ...fonts.fontSize4, color: colors.greyPrimary, margin: "0px" }}>{`/ ${total}`}</p>
          </div>
        </div>
      </div>
      {unit}
    </div> 
  );
};